import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabaseClient = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  }
);

// Helper function to handle Supabase errors consistently
export const handleSupabaseError = (error) => {
  console.error('Supabase error:', error);
  
  if (error?.code === 'PGRST116') {
    return 'Record not found';
  }
  
  if (error?.code === 'PGRST301') {
    return 'Permission denied';
  }
  
  if (error?.code === '23505') {
    return 'Record already exists';
  }
  
  return error?.message || 'An unexpected error occurred';
};

// Helper function to check if user is authenticated
export const requireAuth = async () => {
  const { data: { session }, error } = await supabaseClient.auth.getSession();
  
  if (error || !session) {
    throw new Error('Authentication required');
  }
  
  return session.user;
};

// Helper function to create a standardized response
export const createResponse = (data, error = null) => {
  return {
    data,
    error,
    success: !error
  };
};

// Database table names as constants
export const TABLES = {
  USERS: 'users',
  GROUPS: 'groups',
  MEMBERS: 'members',
  PAYMENTS: 'payments',
  ROTATIONS: 'rotations',
  REMINDERS: 'reminders',
  MORTGAGE_STATEMENTS: 'mortgage_statements',
  PAYOUT_CONFIRMATIONS: 'payout_confirmations',
  NOTIFICATIONS: 'notifications',
  AUDIT_LOGS: 'audit_logs'
};

// Database functions for common operations
export const db = {
  // Generic fetch function
  async fetch(table, options = {}) {
    try {
      let query = supabaseClient.from(table);
      
      if (options.select) {
        query = query.select(options.select);
      }
      
      if (options.filter) {
        Object.entries(options.filter).forEach(([key, value]) => {
          query = query.eq(key, value);
        });
      }
      
      if (options.order) {
        query = query.order(options.order.column, { ascending: options.order.ascending ?? true });
      }
      
      if (options.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return createResponse(data);
    } catch (error) {
      return createResponse(null, handleSupabaseError(error));
    }
  },
  
  // Generic insert function
  async insert(table, data) {
    try {
      const { data: result, error } = await supabaseClient
        .from(table)
        .insert(data)
        .select()
        .single();
      
      if (error) throw error;
      
      return createResponse(result);
    } catch (error) {
      return createResponse(null, handleSupabaseError(error));
    }
  },
  
  // Generic update function
  async update(table, id, data) {
    try {
      const { data: result, error } = await supabaseClient
        .from(table)
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return createResponse(result);
    } catch (error) {
      return createResponse(null, handleSupabaseError(error));
    }
  },
  
  // Generic delete function
  async delete(table, id) {
    try {
      const { error } = await supabaseClient
        .from(table)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return createResponse({ deleted: true });
    } catch (error) {
      return createResponse(null, handleSupabaseError(error));
    }
  }
};

export default supabaseClient;
